import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert, Row, Col, Form, Button, Pagination, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faFilter, faSync, faTrash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function AuditLogsPage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Filtri
    const [actionFilter, setActionFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [daysFilter, setDaysFilter] = useState('30');
    const [availableActions, setAvailableActions] = useState([]);
    
    // Paginazione
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [total, setTotal] = useState(0);
    
    // Modal dettagli
    const [showModal, setShowModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);

    useEffect(() => {
        fetchLogs();
        fetchAvailableActions();
    }, [page, actionFilter, userFilter, daysFilter]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('access_token');
            
            const params = new URLSearchParams();
            params.append('skip', ((page - 1) * pageSize).toString());
            params.append('limit', pageSize.toString());
            if (actionFilter) params.append('action', actionFilter);
            if (userFilter) params.append('user_email', userFilter);
            if (daysFilter) params.append('days', daysFilter);
            
            const response = await axios.get(
                `${API_URL}/api/audit-logs?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setLogs(response.data.logs);
            setTotal(response.data.total);
        } catch (err) {
            console.error('Error fetching logs:', err);
            setError('Errore nel caricamento dei log');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableActions = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(
                `${API_URL}/api/audit-logs/actions`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAvailableActions(response.data);
        } catch (err) {
            console.error('Error fetching actions:', err);
        }
    };

    const handleCleanup = async () => {
        if (!window.confirm('Eliminare i log più vecchi di 365 giorni?')) return;
        
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(
                `${API_URL}/api/audit-logs/cleanup`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccess(response.data.message);
            fetchLogs();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Errore durante il cleanup');
        }
    };

    const handleReset = () => {
        setActionFilter('');
        setUserFilter('');
        setDaysFilter('30');
        setPage(1);
    };

    const getActionBadge = (action) => {
        const badgeMap = {
            valutazione_eliminata: 'danger',
            valutazione_approvata: 'success',
            valutazione_rifiutata: 'warning',
            recensione_eliminata: 'danger',
            recensione_approvata: 'success',
            recensione_rifiutata: 'warning',
            punti_assegnati_manuale: 'info',
            punti_revocati_manuale: 'warning',
            punti_azzerati_tutti: 'danger',
            ordine_modificato: 'info',
            ordine_eliminato: 'danger',
            cliente_modificato: 'info',
            cliente_eliminato: 'danger',
            configurazione_modificata: 'primary'
        };
        return badgeMap[action] || 'secondary';
    };

    const formatAction = (action) => {
        return action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const openDetailsModal = (log) => {
        setSelectedLog(log);
        setShowModal(true);
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div className="container-fluid py-4">
            <Row className="mb-4">
                <Col>
                    <h1>
                        <FontAwesomeIcon icon={faHistory} className="me-3 text-primary" />
                        Log di Audit
                    </h1>
                    <p className="text-muted">
                        Tracciamento delle azioni importanti nel sistema
                    </p>
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={handleCleanup}
                    >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Cleanup Vecchi Log
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Filtri */}
            <Card className="mb-4">
                <Card.Header>
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    Filtri
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Azione</Form.Label>
                                <Form.Select 
                                    value={actionFilter} 
                                    onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="">Tutte le azioni</option>
                                    {availableActions.map(action => (
                                        <option key={action} value={action}>
                                            {formatAction(action)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Utente</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Email utente..."
                                    value={userFilter}
                                    onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Periodo</Form.Label>
                                <Form.Select 
                                    value={daysFilter} 
                                    onChange={(e) => { setDaysFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="">Tutti i log</option>
                                    <option value="1">Ultimo giorno</option>
                                    <option value="7">Ultimi 7 giorni</option>
                                    <option value="30">Ultimi 30 giorni</option>
                                    <option value="90">Ultimi 90 giorni</option>
                                    <option value="365">Ultimo anno</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Button 
                                variant="outline-secondary" 
                                onClick={handleReset}
                                className="me-2"
                            >
                                Reset
                            </Button>
                            <Button variant="primary" onClick={() => { setPage(1); fetchLogs(); }}>
                                <FontAwesomeIcon icon={faSync} className="me-2" />
                                Aggiorna
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tabella Log */}
            <Card>
                <Card.Header>
                    <strong>Log di Audit</strong>
                    {total > 0 && <Badge bg="secondary" className="ms-2">{total} totali</Badge>}
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center p-4">
                            <Spinner animation="border" /> Caricamento log...
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            Nessun log trovato
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead className="table-light">
                                <tr>
                                    <th style={{width: '160px'}}>Data/Ora</th>
                                    <th style={{width: '180px'}}>Azione</th>
                                    <th style={{width: '180px'}}>Utente</th>
                                    <th>Descrizione</th>
                                    <th style={{width: '80px'}}>Dettagli</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id}>
                                        <td>
                                            <small>{formatDate(log.timestamp)}</small>
                                        </td>
                                        <td>
                                            <Badge bg={getActionBadge(log.action)}>
                                                {formatAction(log.action)}
                                            </Badge>
                                        </td>
                                        <td>
                                            <small className="text-muted">{log.user_email}</small>
                                        </td>
                                        <td>
                                            <div style={{ maxWidth: '400px' }}>
                                                {log.description}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            {log.details && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-info"
                                                    onClick={() => openDetailsModal(log)}
                                                >
                                                    <FontAwesomeIcon icon={faInfoCircle} />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Paginazione */}
            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                        <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
                        
                        {[...Array(Math.min(10, totalPages))].map((_, idx) => {
                            const pageNum = idx + 1;
                            return (
                                <Pagination.Item
                                    key={pageNum}
                                    active={pageNum === page}
                                    onClick={() => setPage(pageNum)}
                                >
                                    {pageNum}
                                </Pagination.Item>
                            );
                        })}
                        
                        <Pagination.Next onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                        <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                    </Pagination>
                </div>
            )}

            {/* Modal Dettagli */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Dettagli Log</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <div>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>ID:</strong> {selectedLog.id}
                                </Col>
                                <Col md={6}>
                                    <strong>Data/Ora:</strong> {formatDate(selectedLog.timestamp)}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Azione:</strong> <Badge bg={getActionBadge(selectedLog.action)}>{formatAction(selectedLog.action)}</Badge>
                                </Col>
                                <Col md={6}>
                                    <strong>Utente:</strong> {selectedLog.user_email}
                                </Col>
                            </Row>
                            {selectedLog.ip_address && (
                                <Row className="mb-3">
                                    <Col>
                                        <strong>IP:</strong> {selectedLog.ip_address}
                                    </Col>
                                </Row>
                            )}
                            <Row className="mb-3">
                                <Col>
                                    <strong>Descrizione:</strong>
                                    <div className="border p-2 mt-1 rounded bg-light">
                                        {selectedLog.description}
                                    </div>
                                </Col>
                            </Row>
                            {selectedLog.details && (
                                <Row>
                                    <Col>
                                        <strong>Dettagli Tecnici:</strong>
                                        <pre className="border p-2 mt-1 rounded bg-light" style={{fontSize: '12px', maxHeight: '300px', overflow: 'auto'}}>
                                            {JSON.stringify(selectedLog.details, null, 2)}
                                        </pre>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Chiudi
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
